import React, { useEffect, useState } from "react";
import NoDataView from "../Shared/NoDataView";
import { ErrorAlert, SuccessAlert, WarningAlert } from "../../utils/Alerts";
import { handleReadingFile } from "../../utils/FileHandler";
import { AgGridReact, AgGridColumn } from "ag-grid-react";
import { Trans, useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import LogCapture from "../../utils/LogManagement/LogHelper";
import BaseModal from "./BaseModal";

const ReferenceModal = ({
    isVisible,
    setIsVisible,
    dataReference,
    setReferenceData,
    data,
    setDataReferenceFileName,
    setReferenceProgressBarValue,
    setModifiedDataReference,
    setHasMissingRef,
    entity = "standalone",
    predictionHeader,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isRowsRendered, setIsRowsRendered] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);

    const [gridApi, setGridApi] = useState(null);
    const [, setGridColumnApi] = useState(null);

    const [filename, setFilename] = useState(null);
    const [generatedData, setGeneratedData] = useState(null);
    const [sampleNames, setSampleNames] = useState(null);

    const { t } = useTranslation();

    useEffect(() => {
        if (isEmpty(dataReference)) {
            resetReferenceView();
        }
    }, [dataReference]);

    useEffect(() => {
        resetReferenceView();
    }, [data, predictionHeader]);

    const resetReferenceView = () => {
        if (data) {
            let uniqueSamplesObject = [];
            const newSampleNames = getUniqueSampleNames().map((sampleName) => {
                uniqueSamplesObject.push(sampleName);
                return { sampleName, [predictionHeader]: "--" };
            });
            const headers = [
                "sampleName",
                predictionHeader || "Add your References here....",
            ];

            setSampleNames(uniqueSamplesObject);
            setGeneratedData({ rows: newSampleNames, headers });
            setIsRowsRendered(true);
        }
    };

    const referenceRowIterations = (modifiedData, row) => {
        let isSampleFound = false;

        for (let index = 0; index < generatedData.rows?.length; index++) {
            const referenceRow = generatedData.rows[index];
            if (index === 0) {
                modifiedData.headers = Object.keys(referenceRow).splice(1);
            }

            const sampleName = referenceRow["SampleName"];
            const rowSampleName = row[0].toLowerCase();

            if (!sampleName) {
                return false;
            }

            if (rowSampleName.includes(sampleName.toLowerCase())) {
                isSampleFound = true;
                modifiedData.rows?.push(Object.values(referenceRow).splice(1));
                break;
            }
        }
        if (!isSampleFound) {
            modifiedData.rows?.push(
                Object.values(referenceRow)
                    .splice(1)
                    .map(() => "---")
            );
        }

        return true;
    };

    const modifyData = async () => {
        const modifiedData = { headers: [], rows: [] };
        let isDataValid = true;

        if (data && data.rows) {
            data.rows.forEach((row) => {
                isDataValid = referenceRowIterations(modifiedData, row);
            });

            if (!isDataValid) {
                ErrorAlert(t("fileFormatSampleName"));
                return isDataValid;
            }
        }

        setModifiedDataReference && setModifiedDataReference(modifiedData);
        return isDataValid;
    };

    const handleSubmit = () => {
        modifyData().then((isDataValid) => {
            if (isDataValid) {
                setReferenceData(generatedData);
                if (setDataReferenceFileName)
                    setDataReferenceFileName(filename);
                SuccessAlert(t("referenceImported"));
            }
            setIsVisible(false);
            setIsSubmitting(false);
        });
    };

    const handleFileClick = (id) => {
        const element = document.getElementById(id);
        element.click();
        element.value = "";
    };

    const handleFileRead = (event) => {
        handleReadingFile(
            event,
            () => {
                ErrorAlert(t("readingFileError"));
                LogCapture.error(
                    "Unable to read reference file for measurements"
                );
            },
            (key, retrievedFilename, result) => {
                generateData(result);
                LogCapture.info(
                    `User imported reference file for measurements, filename: ${retrievedFilename}`
                );
                setFilename(retrievedFilename);
                setReferenceProgressBarValue &&
                    setReferenceProgressBarValue(100);
            },
            "reference"
        );
    };

    const newHeadersIterator = (newHeaders, firstHeader, element, row) => {
        let hasMissingValues = false;
        newHeaders.forEach((header, headerIndex) => {
            if (headerIndex === 0) row[firstHeader] = element[headerIndex];
            else {
                const refNumber = +element[headerIndex];
                row[header] = refNumber || "--";
                if (isNaN(refNumber) || refNumber <= 0) {
                    hasMissingValues = true;
                }
            }
        });
        return hasMissingValues;
    };

    const generateData = ({ headers, rows }) => {
        let newData = [];
        let newRows = [...rows];
        let firstHeader = "SampleName";
        let newHeaders = [...headers];
        let hasMissingValues = false;

        if (predictionHeader) {
            const indexOfPrediction = headers.indexOf(predictionHeader);
            if (indexOfPrediction < 0) {
                ErrorAlert("Missing prediction");
                return;
            } else {
                newHeaders = [headers[0], headers[indexOfPrediction]];
                newRows = rows.map((row) => [row[0], row[indexOfPrediction]]);
            }
        }

        if (headers.length < 2) {
            ErrorAlert(t("noParametersFound"));
            setIsInvalid(true);
            return;
        }

        newRows.forEach((element) => {
            const row = {};
            hasMissingValues = newHeadersIterator(
                newHeaders,
                firstHeader,
                element,
                row
            );
            newData.push(row);
        });

        let hasMissingSamples = false;
        newHeaders.shift();

        // get object of empty rows
        const emptyRow = {};
        newHeaders.forEach((header) => {
            emptyRow[header] = "--";
        });

        for (const sampleName of sampleNames) {
            const isFound = newRows.find((row) =>
                sampleName.toLowerCase().includes(row[0].toLowerCase())
            );

            if (!isFound) {
                hasMissingSamples = true;
                newData.push({
                    [firstHeader]: sampleName,
                    ...emptyRow,
                });
            }
        }

        if (hasMissingValues || hasMissingSamples) {
            WarningAlert(t("importMissingValues"));
        }

        setHasMissingRef && setHasMissingRef(false); //hasMissingValues || hasMissingSamples
        setIsInvalid(false); //hasMissingValues || hasMissingSamples
        setGeneratedData({
            headers: [firstHeader, ...newHeaders],
            rows: newData,
        });
        gridApi.setRowData(newData);
    };

    const exportFile = () => {
        LogCapture.info(
            "Reference file for measurements is exported successfully"
        );
        gridApi.exportDataAsCsv();
        SuccessAlert(t("fileExported"));
    };

    const getUniqueSampleNames = () => {
        const uniqueSampleNames = new Set();
        data.rows?.forEach((a) => uniqueSampleNames.add(a[0]));
        return [...uniqueSampleNames];
    };

    const onGridReady = (params) => {
        setGridApi(params.api);
        setGridColumnApi(params.columnApi);
        params.api.setRowData(generatedData.rows);
    };

    const getWidth = (index) => {
        if (!index) {
            return 225;
        } else if (generatedData.headers.length <= 2) {
            return 600;
        } else {
            return 150;
        }
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                title={t("importReference")}
                titleIcon="fas fa-upload"
                action={{
                    name: t("done"),
                    submitAction: handleSubmit,
                    disabled: isSubmitting || isInvalid,
                }}
            >
                <div className="main-content-header flex items-start justify-between">
                    <p className="mr-4">
                        <Trans i18nKey="scansImportMessage">
                            You can export the sample names of your Measurements
                            to help you get their references, then import them.
                        </Trans>
                    </p>

                    <div className="flex items-center">
                        <button
                            className="default-orange-outline-btn flex items-center"
                            onClick={() =>
                                handleFileClick(`${entity}-openFile-2`)
                            }
                            id="import-references-modal-btn"
                        >
                            <i className="fas fa-file-upload" />
                            <span className="ml-2">
                                <Trans i18nKey="import">Import</Trans>
                            </span>
                            <input
                                id={`${entity}-openFile-2`}
                                type="file"
                                name="data"
                                accept=".csv"
                                className="hidden"
                                onChange={handleFileRead}
                            />
                        </button>
                        <button
                            id={`${entity}`}
                            onClick={exportFile}
                            className="default-orange-outline-btn flex items-center ml-2"
                        >
                            <div className="flex items-center">
                                <i className="fas fa-file-download" />
                                <span className="ml-2">
                                    <Trans i18nKey="export">Export</Trans>
                                </span>
                            </div>
                        </button>
                    </div>
                </div>
                <div className="shadow sm:rounded-lg">
                    {!isRowsRendered ? (
                        <NoDataView
                            entity={t("rows")}
                            clickCallback={() => setHandleModal(true)}
                        />
                    ) : (
                        <div
                            style={{
                                height: 300,
                            }}
                            className="ag-theme-alpine text-center"
                        >
                            <AgGridReact
                                overlayLoadingTemplate="<span className='ag-overlay-loading-center'>Please wait while your rows are loading</span>"
                                rowSelection="single"
                                onGridReady={onGridReady}
                                rowData={generatedData.rows}
                            >
                                {generatedData.headers.map((header, index) => {
                                    return (
                                        <AgGridColumn
                                            key={`${entity}-${index}`}
                                            field={header}
                                            width={getWidth(index)}
                                            cellClass="text-gray-500"
                                            pinned={!index ? true : false}
                                        />
                                    );
                                })}
                            </AgGridReact>
                        </div>
                    )}
                </div>
            </BaseModal>
        </>
    );
};

export default ReferenceModal;
